import React, { useState } from 'react';
import {
    Badge,
    Button,
    Card,
    Col,
    Input,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    ApiOutlined,
    EditOutlined,
    SearchOutlined,
    SyncOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import { BranchSummary, useBranches, useSyncBranch, useUpdateDistrict } from '../hooks/useBranches';

const { Title, Text } = Typography;
const { Option } = Select;

const STATUS_COLORS: Record<string, string> = {
    SYNCED: 'success',
    PENDING: 'processing',
    FAILED: 'error',
};

const BranchManagementView: React.FC = () => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [search, setSearch] = useState('');
    const [editTarget, setEditTarget] = useState<BranchSummary | null>(null);
    const [districtCode, setDistrictCode] = useState('');

    const { data, isLoading, isError, refetch } = useBranches({ page, size: pageSize, status: statusFilter });
    const syncBranch = useSyncBranch();
    const updateDistrict = useUpdateDistrict();

    const filteredContent = (data?.data?.content ?? []).filter(b =>
        !search ||
        b.branchName?.toLowerCase().includes(search.toLowerCase()) ||
        b.branchCode?.toLowerCase().includes(search.toLowerCase()) ||
        b.districtCode?.toLowerCase().includes(search.toLowerCase())
    );

    const handleSyncAll = () => {
        syncBranch.mutate(undefined, {
            onSuccess: (res) => {
                if (res.data?.success) {
                    message.success(res.data.message ?? 'All active branches synced to ESS');
                } else {
                    message.warning(res.data?.message ?? 'Sync returned failure');
                }
            },
            onError: (err: any) => {
                message.error(err?.response?.data?.message ?? 'Failed to sync all branches');
            },
        });
    };

    const handleUpdateDistrict = () => {
        if (!editTarget) return;
        updateDistrict.mutate(
            { branchCode: editTarget.branchCode, districtCode },
            {
                onSuccess: (res) => {
                    if (res.data?.success) {
                        message.success(`District updated for "${editTarget.branchName}"`);
                        setEditTarget(null);
                        setDistrictCode('');
                    } else {
                        message.error(res.data?.message ?? 'Update failed');
                    }
                },
                onError: (err: any) => {
                    message.error(err?.response?.data?.message ?? 'Failed to update district');
                },
            }
        );
    };

    const columns = [
        {
            title: 'Branch Code',
            dataIndex: 'branchCode',
            key: 'branchCode',
            sorter: (a: BranchSummary, b: BranchSummary) =>
                (a.branchCode ?? '').localeCompare(b.branchCode ?? ''),
        },
        {
            title: 'Branch Name',
            dataIndex: 'branchName',
            key: 'branchName',
            sorter: (a: BranchSummary, b: BranchSummary) =>
                (a.branchName ?? '').localeCompare(b.branchName ?? ''),
        },
        {
            title: 'District Code',
            dataIndex: 'districtCode',
            key: 'districtCode',
            render: (v: string) => v ?? <Text type="secondary">—</Text>,
        },
        {
            title: 'ESS Status',
            dataIndex: 'emkopoSyncStatus',
            key: 'emkopoSyncStatus',
            render: (status: string) => (
                <Tag color={STATUS_COLORS[status] ?? 'default'}>{status}</Tag>
            ),
        },
        {
            title: 'Last Sync',
            dataIndex: 'emkopoLastSyncDate',
            key: 'emkopoLastSyncDate',
            render: (v: string | null) =>
                v ? new Date(v).toLocaleString() : <Text type="secondary">—</Text>,
        },
        {
            title: 'Active',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (v: boolean) =>
                v ? <Badge status="success" text="Yes" /> : <Badge status="default" text="No" />,
        },
        {
            title: 'Last Error',
            dataIndex: 'emkopoLastSyncError',
            key: 'emkopoLastSyncError',
            render: (v: string | null) =>
                v ? (
                    <Tooltip title={v}>
                        <Text type="danger" ellipsis style={{ maxWidth: 200 }}>
                            <WarningOutlined /> {v}
                        </Text>
                    </Tooltip>
                ) : (
                    <Text type="secondary">—</Text>
                ),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: BranchSummary) => (
                <Space size="small">
                    <Tooltip title="Update district code">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => {
                                setEditTarget(record);
                                setDistrictCode(record.districtCode ?? '');
                            }}
                        >
                            Update District
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <Card>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Title level={4}>
                        <ApiOutlined style={{ marginRight: 8 }} />
                        ESS Branch Management
                    </Title>
                    <Text type="secondary">
                        Manage branch synchronization with ESS and update district codes.
                    </Text>
                </Col>
                <Col>
                    <Space>
                        <Tooltip title="Sync all active branches to ESS">
                            <Button icon={<SyncOutlined />} onClick={handleSyncAll} loading={syncBranch.isPending}>
                                Sync All Branches
                            </Button>
                        </Tooltip>
                        <Tooltip title="Refresh">
                            <Button icon={<SyncOutlined />} onClick={() => refetch()} loading={isLoading} />
                        </Tooltip>
                    </Space>
                </Col>
            </Row>

            <Row gutter={12} style={{ marginBottom: 16 }}>
                <Col>
                    <Input
                        placeholder="Search by name / code / district"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        allowClear
                        style={{ width: 300 }}
                    />
                </Col>
                <Col>
                    <Select
                        placeholder="Filter by status"
                        allowClear
                        style={{ width: 180 }}
                        value={statusFilter}
                        onChange={v => { setStatusFilter(v); setPage(0); }}
                    >
                        <Option value="SYNCED">Synced</Option>
                        <Option value="PENDING">Pending</Option>
                        <Option value="FAILED">Failed</Option>
                    </Select>
                </Col>
            </Row>

            {isError && (
                <Text type="danger">Failed to load branches. Please try again.</Text>
            )}

            <Table
                columns={columns}
                dataSource={filteredContent}
                rowKey="branchCode"
                loading={isLoading}
                pagination={{
                    current: page + 1,
                    pageSize,
                    total: data?.data?.totalElements ?? 0,
                    onChange: (p, ps) => { setPage(p - 1); setPageSize(ps); },
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} branches`,
                }}
            />

            <Modal
                title={
                    <Space>
                        <EditOutlined />
                        Update District Code
                    </Space>
                }
                open={!!editTarget}
                onCancel={() => { setEditTarget(null); setDistrictCode(''); }}
                onOk={handleUpdateDistrict}
                okText="Update"
                okButtonProps={{ loading: updateDistrict.isPending }}
                cancelButtonProps={{ disabled: updateDistrict.isPending }}
            >
                {editTarget && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text>
                            Update district code for branch{' '}
                            <Text strong>"{editTarget.branchName}"</Text> (code:{' '}
                            <Text code>{editTarget.branchCode}</Text>)
                        </Text>
                        <Input
                            placeholder="Enter district code"
                            value={districtCode}
                            onChange={e => setDistrictCode(e.target.value)}
                            maxLength={100}
                        />
                    </Space>
                )}
            </Modal>
        </Card>
    );
};

export default BranchManagementView;
